import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { AttendanceCorrectionDetailFields } from '../../components/AttendanceCorrectionDetailFields';
import { ErrorState } from '../../components/ErrorState';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import {
  ATTENDANCE_CORRECTION_ROUTE_CAPABILITIES,
  getAttendanceCorrectionModuleCapabilities,
} from '../../config/attendanceCorrectionCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  approveAttendanceCorrectionRequest,
  rejectAttendanceCorrectionRequest,
} from '../../services/attendanceCorrectionService';
import { getWorkflowDetails } from '../../services/workflowService';
import type { WorkflowDetails } from '../../types/workflow';
import { workflowErrorMessage } from '../../utils/workflowErrors';
import { attendanceCorrectionErrorMessage } from '../../utils/attendanceCorrectionErrors';
import { confirmAction } from '../../utils/confirmAction';
import {
  isPendingWorkflowActionStatus,
  isRejectedWorkflowStatus,
  isCancelledWorkflowStatus,
} from '../../utils/workflowStatus';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'AttendanceCorrectionApproval'
>;

export function AttendanceCorrectionApprovalScreen({
  navigation,
  route,
}: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getAttendanceCorrectionModuleCapabilities(
    auth.permissions,
  );
  const workflowInstanceId = route.params.workflowInstanceId;

  const [details, setDetails] = useState<WorkflowDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const actionLockRef = useRef(false);

  const canAct =
    ATTENDANCE_CORRECTION_ROUTE_CAPABILITIES.approveAction &&
    ATTENDANCE_CORRECTION_ROUTE_CAPABILITIES.rejectAction &&
    isPendingWorkflowActionStatus(details?.workflowStatusCode);
  const isRejected = isRejectedWorkflowStatus(details?.workflowStatusCode);
  const isCancelled = isCancelledWorkflowStatus(details?.workflowStatusCode);
  const correctionRequestId = useMemo(() => {
    const fromDetails = Number(
      details?.requestDetails?.correctionRequestId ??
        details?.requestDetails?.attendanceCorrectionRequestId ??
        details?.requestDetails?.entityId,
    );
    if (Number.isInteger(fromDetails) && fromDetails > 0) return fromDetails;
    const fromWorkflow = details?.requestReferenceId ?? null;
    return fromWorkflow != null && fromWorkflow > 0 ? fromWorkflow : null;
  }, [details]);

  const load = useCallback(
    async (isRefresh = false) => {
      if (!capabilities.canAccessCorrectionApproval) {
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!Number.isInteger(workflowInstanceId) || workflowInstanceId <= 0) {
        setDetails(null);
        setError('Attendance correction request was not found.');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const next = await getWorkflowDetails(workflowInstanceId);
        const code = (next.requestTypeCode || '').toUpperCase();
        if (code && code !== 'ATTENDANCE_CORRECTION') {
          if (!isRefresh) setDetails(null);
          setError('This approval is not an attendance correction request.');
        } else {
          setDetails(next);
          setError(null);
        }
      } catch (caught) {
        if (!isRefresh) setDetails(null);
        setError(
          workflowErrorMessage(
            caught,
            'Unable to load attendance correction approval details.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [capabilities.canAccessCorrectionApproval, workflowInstanceId],
  );

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const handleApprove = () => {
    if (!canAct || acting || actionLockRef.current || correctionRequestId == null) {
      return;
    }
    confirmAction({
      title: 'Approve correction',
      message: 'Do you want to approve this attendance correction request?',
      confirmText: 'Approve',
      onConfirm: () => {
        if (actionLockRef.current) return;
        actionLockRef.current = true;
        setActing(true);
        setActionError(null);
        return approveAttendanceCorrectionRequest(correctionRequestId)
          .then(() => {
            // Only leave the screen after the backend confirms approval.
            navigation.goBack();
          })
          .catch(caught => {
            setActionError(
              attendanceCorrectionErrorMessage(
                caught,
                'Unable to approve attendance correction request.',
              ),
            );
          })
          .finally(() => {
            actionLockRef.current = false;
            setActing(false);
          });
      },
    });
  };

  const handleReject = async () => {
    if (!canAct || acting || actionLockRef.current || correctionRequestId == null) {
      return;
    }
    const trimmedReason = rejectReason.trim();
    if (!trimmedReason) {
      setActionError('Rejection reason is required.');
      return;
    }
    actionLockRef.current = true;
    setActing(true);
    setActionError(null);
    try {
      await rejectAttendanceCorrectionRequest(
        correctionRequestId,
        trimmedReason,
      );
      navigation.goBack();
    } catch (caught) {
      setActionError(
        attendanceCorrectionErrorMessage(
          caught,
          'Unable to reject attendance correction request.',
        ),
      );
    } finally {
      actionLockRef.current = false;
      setActing(false);
    }
  };

  const employeeName =
    details?.employee.displayName ||
    details?.employee.employeeNumber ||
    'Employee unavailable';
  const requestDetails = details?.requestDetails ?? null;

  const themed = useMemo(
    () => ({
      name: { ...typography.sectionTitle, color: palette.textPrimary },
      meta: { ...typography.caption, color: palette.textSecondary },
      actionError: {
        ...typography.caption,
        color: palette.error,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.ATTENDANCE_APPROVE}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Correction request detail"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading attendance correction approval" />
        </AppScreen>
      ) : error && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Correction request detail"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : details ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                load(true).catch(() => undefined);
              }}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader title={employeeName} onBack={navigation.goBack} />

          <View style={styles.body}>
            <AppCard style={styles.card}>
              <View style={styles.profileRow}>
                <ProfileAvatar name={employeeName} size={56} />
                <View style={styles.profileText}>
                  <Text style={themed.name}>{employeeName}</Text>
                  {details.employee.employeeNumber ? (
                    <Text style={themed.meta}>
                      {details.employee.employeeNumber}
                    </Text>
                  ) : null}
                  {details.employee.designationName ? (
                    <Text style={themed.meta}>
                      {details.employee.designationName}
                    </Text>
                  ) : null}
                </View>
                <LeaveStatusBadge
                  label={details.workflowStatusLabel}
                  statusCode={details.workflowStatusCode}
                />
              </View>

              <AttendanceCorrectionDetailFields
                requestDetails={requestDetails}
                submittedAt={details.submittedAt}
                rejectReason={details.rejectReason}
                cancellationReason={details.cancellationReason}
                showRejectReason={isRejected}
                showCancellationReason={isCancelled}
                fallbackEditorName={details.employee.displayName}
                palette={palette}
              />
            </AppCard>

            {actionError ? (
              <Text accessibilityRole="alert" style={themed.actionError}>
                {actionError}
              </Text>
            ) : null}

            {canAct && rejectMode ? (
              <View style={styles.rejectBlock}>
                <AppInput
                  label="Rejection reason"
                  value={rejectReason}
                  onChangeText={value => {
                    setRejectReason(value);
                    setActionError(null);
                  }}
                  placeholder="Enter reason for rejection"
                />
                <AppButton
                  title="Confirm Reject"
                  variant="danger"
                  onPress={handleReject}
                  loading={acting}
                  disabled={acting}
                />
                <AppButton
                  title="Cancel"
                  variant="text"
                  onPress={() => {
                    setRejectMode(false);
                    setRejectReason('');
                    setActionError(null);
                  }}
                  disabled={acting}
                />
              </View>
            ) : canAct ? (
              <View style={styles.actions}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Reject"
                  accessibilityState={{ disabled: !canAct || acting }}
                  disabled={!canAct || acting}
                  onPress={() => {
                    setRejectMode(true);
                    setActionError(null);
                  }}
                  style={[
                    styles.actionButton,
                    styles.rejectButton,
                    {
                      borderColor: palette.error,
                      opacity: canAct && !acting ? 1 : 0.45,
                    },
                  ]}
                >
                  <Text style={[styles.rejectLabel, { color: palette.error }]}>
                    Reject
                  </Text>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Approve"
                  accessibilityState={{ disabled: !canAct || acting }}
                  disabled={!canAct || acting}
                  onPress={handleApprove}
                  style={[
                    styles.actionButton,
                    {
                      backgroundColor: palette.success,
                      borderColor: palette.success,
                      opacity: canAct && !acting ? 1 : 0.45,
                    },
                  ]}
                >
                  <Text style={[styles.approveLabel, { color: palette.onSuccess }]}>
                    Approve
                  </Text>
                </Pressable>
              </View>
            ) : null}
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: 0, marginBottom: spacing.md, paddingBottom: spacing.sm },
  profileRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.md,
    minWidth: 0,
  },
  profileText: { flex: 1, gap: 2, minWidth: 0 },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  rejectBlock: {
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  actionButton: {
    flexGrow: 1,
    flexBasis: 140,
    minWidth: 140,
    maxWidth: '100%',
    minHeight: 50,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rejectButton: {
    backgroundColor: 'transparent',
  },
  rejectLabel: {
    ...typography.button,
    fontWeight: '700',
  },
  approveLabel: {
    ...typography.button,
    fontWeight: '700',
  },
});
